import { configureStore } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  apiMiscellaneousSlice,
  initSliceMeta as initApiMiscellaneous,
} from '@vs/rtk/slices/apiMiscellaneous';
import { initSliceMeta as initMvbSubscriptionPlanSliceMeta } from '@vs/rtk/slices/apiMvbSubscription';
import { mvbSubscriptionPlanApiInjected } from '@vs/rtk/slices/apiMvbSubscription';
import {
  initSliceMeta as initAccount2SliceMeta,
  vsAccount2ApiSlice,
} from '@vs/rtk/slices/apiVsAccount2';
import {
  initSliceMeta as initAuthSliceMeta,
  vsAuthApiSlice,
} from '@vs/rtk/slices/apiVsAuth';
import {
  initSliceMeta as initCommonSliceMeta,
  vsCommonApiSlice,
} from '@vs/rtk/slices/apiVsCommon';
import {
  initSliceMeta as initEntity2SliceMeta,
  vsEntity2ApiInjected,
} from '@vs/rtk/slices/apiVsEntity2';
import {
  initSliceMeta as initSubscriptionSliceMeta,
  vsSubscriptionApiSlice,
} from '@vs/rtk/slices/apiVsSubscription';
import { BaseGlobalState } from '@vs/rtk/slices/genericGlobal';
import { dispatchUnAuthErrorDialog } from '@vs/rtk/utils';
import localforage from 'localforage';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from 'redux-persist';

import { deployEnv, isLocalDevMode } from '../constants';
import { env } from '../constants/env';
import getUserAccessToken from '../utils/getUserAccessToken';
import getUserId from '../utils/getUserId';
import { rootReducer } from './reducer';

function getReduxPersistKey() {
  return `${deployEnv}-vs-account`;
}

const meta = {
  deployEnv,
  getTokenFunc: getUserAccessToken,
  getUserIdFunc: getUserId,
};

initCommonSliceMeta({ ...meta, baseUrl: env.apiEndpoint.vs });
initAccount2SliceMeta({
  ...meta,
  baseUrl: env.apiEndpoint.vs,
  ssoBaseUrl: env.apiEndpoint.vsSso,
});
initAuthSliceMeta({ ...meta, baseUrl: env.apiEndpoint.vsAuth });
initSubscriptionSliceMeta({
  ...meta,
  env,
  baseUrl: env.apiEndpoint.vsSubscription,
});
initApiMiscellaneous({ ...meta, baseUrl: env.apiEndpoint.vs, env });
initMvbSubscriptionPlanSliceMeta({ ...meta, baseUrl: env.apiEndpoint.mvb });
initEntity2SliceMeta({
  ...meta,
  baseUrl: env.apiEndpoint.vs,
});

const key = getReduxPersistKey();
const indexedDbCacheExpiredIn = 1 * 60 * 60 * 1000; // 1 hour

const whitelist: string[] = [vsSubscriptionApiSlice.reducerPath];

type ReducerRoot = ReturnType<typeof rootReducer>;
type ReducerRootState = ReducerRoot[keyof ReducerRoot];
type ApiSliceState = Exclude<ReducerRootState, BaseGlobalState>;

const persistTransform = createTransform(
  (inboundState: ApiSliceState) => inboundState,
  (outboundState: ApiSliceState, key) => {
    const queries = outboundState.queries;
    Object.entries(queries).forEach(([endpoint, config]) => {
      if (config) {
        if (config.status !== QueryStatus.fulfilled) {
          delete queries[endpoint];
        } else if (
          Number.isInteger(config.fulfilledTimeStamp) &&
          Date.now() - config.fulfilledTimeStamp > indexedDbCacheExpiredIn
        ) {
          delete queries[endpoint];
        }
      }
    });
    return { queries } as ApiSliceState;
  },
  { whitelist }
);

export const store = configureStore({
  devTools: isLocalDevMode && { name: 'vs-account-store' },
  reducer: persistReducer(
    {
      key,
      version: 1,
      storage: localforage,
      whitelist,
      transforms: [persistTransform],
    },
    rootReducer
  ) as unknown as typeof rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      /* Opt-out these actions when use redux persist with Redux toolkit, see issue:
         https://github.com/rt2zz/redux-persist/issues/1455
      */
      serializableCheck: {
        ignoredActions: [FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE],
      },
    }).concat([
      vsAccount2ApiSlice.middleware,
      vsCommonApiSlice.middleware,
      vsAuthApiSlice.middleware,
      vsSubscriptionApiSlice.middleware,
      apiMiscellaneousSlice.middleware,
      mvbSubscriptionPlanApiInjected.middleware,
      vsEntity2ApiInjected.middleware,
      dispatchUnAuthErrorDialog,
    ]),
});

export const persistor = persistStore(store);

if (isLocalDevMode && import.meta.hot) {
  import.meta.hot.accept('./reducer.ts', module =>
    store.replaceReducer(
      persistReducer(
        {
          key,
          version: 1,
          storage: localforage,
          whitelist,
        },
        module?.rootReducer
      ) as unknown as typeof rootReducer
    )
  );
}

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;
